import React, { useState, useEffect } from 'react';
import {
  AreaChart, Area, XAxis, YAxis, CartesianGrid,
  Tooltip, Legend, ResponsiveContainer
} from 'recharts';
import { FunnelMetricsResponse, TimeseriesItem } from '../services/metricsApi.ts';
import { PeriodComparison } from './PeriodComparison.tsx';
import { formatCurrency, formatDateRange } from '../utils/formatters.ts';
import { BarChart2Icon, InfoIcon } from 'lucide-react';
import { useTheme } from '../contexts/ThemeContext.tsx';

type OverviewTabProps = {
  funnelMetrics: FunnelMetricsResponse | null;
  timeseriesData: TimeseriesItem[];
  periodComparison: {
    mesAtual: {
      leads: number;
      agendamentos: number;
      comparecimentos: number;
      vendas: number;
    };
    mesAnterior: {
      leads: number;
      agendamentos: number;
      comparecimentos: number;
      vendas: number;
    }
  };
  // Adicionando propriedades para datas
  startDate: Date;
  endDate: Date;
  comparisonStartDate: Date;
  comparisonEndDate: Date;
  // Propriedade para agendamentos por data da consulta
  agendamentosPorDataConsulta?: {
    total: number;
    percentual: number;
  };
};

export const OverviewTab: React.FC<OverviewTabProps> = ({
  funnelMetrics,
  timeseriesData,
  periodComparison,
  startDate,
  endDate,
  comparisonStartDate: propComparisonStartDate,
  comparisonEndDate: propComparisonEndDate,
  agendamentosPorDataConsulta
}) => {
  const { isDark } = useTheme();
  // Estado para controlar o tipo de gráfico (linha ou barras)


  // Estado para armazenar dados de série temporal transformados
  const [transformedTimeseriesData, setTransformedTimeseriesData] = useState<TimeseriesItem[]>([]);

  // Calcular as datas fixas para o comparativo (sempre mês atual vs mês anterior)
  const today = new Date();
  const firstDayCurrentMonth = new Date(today.getFullYear(), today.getMonth(), 1);

  const firstDayPreviousMonth = new Date(today.getFullYear(), today.getMonth() - 1, 1);
  const lastDayPreviousMonthSameDay = new Date(today.getFullYear(), today.getMonth() - 1, today.getDate());

  // Usar datas fixas para o comparativo, ignorando as props
  const fixedComparisonStartDate = firstDayCurrentMonth;
  const fixedComparisonEndDate = today;
  const fixedComparisonPrevStartDate = firstDayPreviousMonth;
  const fixedComparisonPrevEndDate = lastDayPreviousMonthSameDay;

  // Formatação das datas para exibição
  const periodoAtual = formatDateRange(fixedComparisonStartDate, fixedComparisonEndDate);
  const periodoAnterior = formatDateRange(fixedComparisonPrevStartDate, fixedComparisonPrevEndDate);
  const periodoLabel = `${periodoAtual} vs ${periodoAnterior}`;
  const dataAtual = new Date().toLocaleDateString('pt-BR', {
    day: 'numeric',
    month: 'long',
    year: 'numeric'
  });

  // Log para debugar alterações nas props e datas
  useEffect(() => {
    console.log('--------- DATAS OverviewTab ---------');
    console.log('Props startDate:', startDate);
    console.log('Props endDate:', endDate);
    console.log('FunnelMetrics:', funnelMetrics);
    console.log('TimeseriesData:', timeseriesData);
    console.log('AgendamentosPorDataConsulta:', agendamentosPorDataConsulta);
  }, [startDate, endDate, funnelMetrics, timeseriesData, agendamentosPorDataConsulta]);

  // Usar dados de agendamentos por data de consulta, se disponíveis
  const totalAgendamentos = agendamentosPorDataConsulta?.total || funnelMetrics?.totalAgendamentos || 0;
  const percentAgendamentos = agendamentosPorDataConsulta?.percentual || funnelMetrics?.percentAgendamentos || 0;

  // Formatador de valores para os gráficos
  const formatNumberValue = (value: number) => `${value.toLocaleString()}`;

  // Transformar dados de série temporal quando há agendamentos por data de consulta
  // Transformar dados de série temporal para corresponder ao funil
  useEffect(() => {
    console.log('--------- TRANSFORMAÇÃO TIMESERIES ---------');
    console.log('TimeseriesData antes da transformação:', timeseriesData);
    console.log('Valores do funil:', {
      leads: funnelMetrics?.totalLeads || 0,
      agendamentos: totalAgendamentos,
      comparecimentos: funnelMetrics?.totalComparecimentos || 0,
      vendas: funnelMetrics?.totalVendas || 0
    });

    if (!timeseriesData || timeseriesData.length === 0 || !funnelMetrics) {
      console.log('Dados insuficientes para transformação');
      setTransformedTimeseriesData([]);
      return;
    }

    // Calcular totais na série temporal
    const timeseriesLeadsTotal = timeseriesData.reduce((acc, item) => acc + item.totalLeads, 0);
    const timeseriesAgendamentosTotal = timeseriesData.reduce((acc, item) => acc + item.totalAgendamentos, 0);
    const timeseriesComparecimentosTotal = timeseriesData.reduce((acc, item) => acc + item.totalComparecimentos, 0);
    const timeseriesVendasTotal = timeseriesData.reduce((acc, item) => acc + item.totalVendas, 0);

    console.log('Totais na série temporal:', {
      leads: timeseriesLeadsTotal,
      agendamentos: timeseriesAgendamentosTotal,
      comparecimentos: timeseriesComparecimentosTotal,
      vendas: timeseriesVendasTotal
    });

    // Calcular fatores de ajuste para cada métrica
    const leadsFactor = timeseriesLeadsTotal > 0 ? (funnelMetrics.totalLeads / timeseriesLeadsTotal) : 1;
    const agendamentosFactor = timeseriesAgendamentosTotal > 0 ? (totalAgendamentos / timeseriesAgendamentosTotal) : 1;
    const comparecimentosFactor = timeseriesComparecimentosTotal > 0 ? (funnelMetrics.totalComparecimentos / timeseriesComparecimentosTotal) : 1;
    const vendasFactor = timeseriesVendasTotal > 0 ? (funnelMetrics.totalVendas / timeseriesVendasTotal) : 1;

    console.log('Fatores de ajuste:', {
      leads: leadsFactor,
      agendamentos: agendamentosFactor,
      comparecimentos: comparecimentosFactor,
      vendas: vendasFactor
    });

    // Ajustar cada item da série temporal para corresponder aos totais do funil
    const adjusted = timeseriesData.map(item => {
      const newLeads = Math.round(item.totalLeads * leadsFactor);
      const newAgendamentos = Math.round(item.totalAgendamentos * agendamentosFactor);
      const newComparecimentos = Math.round(item.totalComparecimentos * comparecimentosFactor);
      const newVendas = Math.round(item.totalVendas * vendasFactor);

      console.log(`Ajustando mês ${item.month}:`, {
        leads: `${item.totalLeads} -> ${newLeads}`,
        agendamentos: `${item.totalAgendamentos} -> ${newAgendamentos}`,
        comparecimentos: `${item.totalComparecimentos} -> ${newComparecimentos}`,
        vendas: `${item.totalVendas} -> ${newVendas}`
      });

      return {
        ...item,
        totalLeads: newLeads,
        totalAgendamentos: newAgendamentos,
        totalComparecimentos: newComparecimentos,
        totalVendas: newVendas
      };
    });

    // Verificar totais após ajuste
    const adjustedLeadsTotal = adjusted.reduce((acc, item) => acc + item.totalLeads, 0);
    const adjustedAgendamentosTotal = adjusted.reduce((acc, item) => acc + item.totalAgendamentos, 0);
    const adjustedComparecimentosTotal = adjusted.reduce((acc, item) => acc + item.totalComparecimentos, 0);
    const adjustedVendasTotal = adjusted.reduce((acc, item) => acc + item.totalVendas, 0);

    console.log('Totais após ajuste:', {
      leads: adjustedLeadsTotal,
      agendamentos: adjustedAgendamentosTotal,
      comparecimentos: adjustedComparecimentosTotal,
      vendas: adjustedVendasTotal
    });

    console.log('Diferenças com o funil:', {
      leads: adjustedLeadsTotal - funnelMetrics.totalLeads,
      agendamentos: adjustedAgendamentosTotal - totalAgendamentos,
      comparecimentos: adjustedComparecimentosTotal - funnelMetrics.totalComparecimentos,
      vendas: adjustedVendasTotal - funnelMetrics.totalVendas
    });

    setTransformedTimeseriesData(adjusted);
  }, [timeseriesData, funnelMetrics, totalAgendamentos]);

  return (
    <div className="space-y-6">
      {/* Aviso sobre período de comparação fixo */}
      <div className={`border-l-4 p-4 rounded-md ${isDark ? 'bg-blue-900/20 border-blue-500' : 'bg-blue-50 border-blue-500'}`}>
        <div className="flex">
          <div className="flex-shrink-0">
            <InfoIcon className="h-5 w-5 text-blue-500" />
          </div>
          <div className="ml-3">
            <p className={`text-sm ${isDark ? 'text-blue-200' : 'text-blue-700'}`}>
              A comparação abaixo sempre mostra dados do mês atual (1 a {today.getDate()} de {today.toLocaleDateString('pt-BR', { month: 'long' })})
              versus mesmo período do mês anterior, independentemente do filtro selecionado.
            </p>
          </div>
        </div>
      </div>

      {/* Comparativo período atual vs anterior */}
      <PeriodComparison
        mesAtual={periodComparison.mesAtual}
        mesAnterior={periodComparison.mesAnterior}
        periodoLabel={periodoLabel}
        dataAtual={dataAtual}
      />

      {/* Gráficos principais */}
      <div className="grid md:grid-cols-2 gap-6">
        {/* Funil de Conversão */}
        <div className={`rounded-xl shadow-sm border ${isDark ? 'bg-gray-800 border-gray-700' : 'bg-white border-gray-200'}`}>
          <div className={`p-4 border-b ${isDark ? 'border-gray-700' : 'border-gray-100'}`}>
            <h3 className={`text-lg font-semibold ${isDark ? 'text-white' : 'text-gray-800'}`}>Funil de Conversão</h3>
            <p className={`text-sm ${isDark ? 'text-gray-400' : 'text-gray-500'}`}>Taxa de conversão entre cada etapa do funil</p>
          </div>
          <div className="p-4 space-y-6">
            <div className="space-y-1">
              <div className="flex justify-between items-center">
                <span className={`text-sm font-medium ${isDark ? 'text-gray-200' : 'text-gray-700'}`}>Leads gerados</span>
                <span className={`text-sm font-medium ${isDark ? 'text-gray-200' : 'text-gray-700'}`}>{funnelMetrics?.totalLeads || 0}</span>
              </div>
              <div className={`h-2 rounded-full ${isDark ? 'bg-blue-900/30' : 'bg-blue-100'}`}>
                <div className="h-2 bg-blue-500 rounded-full w-full"></div>
              </div>
            </div>

            <div className="space-y-1">
              <div className="flex justify-between items-center">
                <div className="flex items-center">
                  <span className="text-sm font-medium">Agendamentos</span>
                  {agendamentosPorDataConsulta && (
                    <span className="text-xs text-gray-500 ml-1">(data consulta)</span>
                  )}
                </div>
                <div className="flex items-center gap-2">
                  <span className="text-sm font-medium">{totalAgendamentos}</span>
                  <span className="text-xs text-gray-500">
                    ({percentAgendamentos.toFixed(1)}%)
                  </span>
                </div>
              </div>
              <div className="h-2 bg-emerald-100 rounded-full">
                <div
                  className="h-2 bg-emerald-500 rounded-full"
                  style={{ width: `${percentAgendamentos}%` }}
                ></div>
              </div>
            </div>

            <div className="space-y-1">
              <div className="flex justify-between items-center">
                <span className="text-sm font-medium">Comparecimentos</span>
                <div className="flex items-center gap-2">
                  <span className="text-sm font-medium">{funnelMetrics?.totalComparecimentos || 0}</span>
                  <span className="text-xs text-gray-500">
                    ({funnelMetrics?.percentComparecimentos?.toFixed(1) || 0}%)
                  </span>
                </div>
              </div>
              <div className="h-2 bg-violet-100 rounded-full">
                <div
                  className="h-2 bg-violet-500 rounded-full"
                  style={{ width: `${funnelMetrics?.percentComparecimentos || 0}%` }}
                ></div>
              </div>
            </div>

            <div className="space-y-1">
              <div className="flex justify-between items-center">
                <span className="text-sm font-medium">Vendas</span>
                <div className="flex items-center gap-2">
                  <span className="text-sm font-medium">{funnelMetrics?.totalVendas || 0}</span>
                  <span className="text-xs text-gray-500">
                    ({funnelMetrics?.percentVendas?.toFixed(1) || 0}%)
                  </span>
                </div>
              </div>
              <div className="h-2 bg-amber-100 rounded-full">
                <div
                  className="h-2 bg-amber-500 rounded-full"
                  style={{ width: `${funnelMetrics?.percentVendas || 0}%` }}
                ></div>
              </div>
            </div>
          </div>
          <div className={`p-4 border-t flex justify-between ${isDark ? 'border-gray-700' : 'border-gray-100'}`}>
            <div>
              <p className={`text-2xl font-bold ${isDark ? 'text-white' : 'text-gray-900'}`}>{formatCurrency(funnelMetrics?.valorFaturado || 0)}</p>
              <p className={`text-sm ${isDark ? 'text-gray-400' : 'text-gray-500'}`}>Valor faturado</p>
            </div>
            <div>
              <p className={`text-2xl font-bold ${isDark ? 'text-white' : 'text-gray-900'}`}>{formatCurrency(funnelMetrics?.ticketMedio || 0)}</p>
              <p className={`text-sm ${isDark ? 'text-gray-400' : 'text-gray-500'}`}>Ticket médio</p>
            </div>
          </div>
        </div>


        {/* Evolução no tempo */}
        <div className={`rounded-xl shadow-sm border ${isDark ? 'bg-gray-800 border-gray-700' : 'bg-white border-gray-200'}`}>
          <div className={`p-4 border-b ${isDark ? 'border-gray-700' : 'border-gray-100'}`}>
            <h3 className={`text-lg font-semibold ${isDark ? 'text-white' : 'text-gray-800'}`}>Evolução no Tempo</h3>
            <p className={`text-sm ${isDark ? 'text-gray-400' : 'text-gray-500'}`}>
              Desempenho mensal de todas as métricas
              {agendamentosPorDataConsulta && (
                <span className="italic ml-1">(agendamentos por data consulta)</span>
              )}
            </p>
          </div>
          <div className="p-4">


            {/* Implementação do gráfico usando Recharts */}
            <div className="h-72">
              <ResponsiveContainer width="100%" height="100%">
                <AreaChart
                  data={transformedTimeseriesData}
                  margin={{ top: 20, right: 30, left: 0, bottom: 0 }}
                >
                  <defs>
                    <linearGradient id="colorLeads" x1="0" y1="0" x2="0" y2="1">
                      <stop offset="5%" stopColor="var(--sminy-primary)" stopOpacity={0.3} />
                      <stop offset="95%" stopColor="var(--sminy-primary)" stopOpacity={0} />
                    </linearGradient>
                    <linearGradient id="colorAgendamentos" x1="0" y1="0" x2="0" y2="1">
                      <stop offset="5%" stopColor="#10b981" stopOpacity={0.3} />
                      <stop offset="95%" stopColor="#10b981" stopOpacity={0} />
                    </linearGradient>
                    <linearGradient id="colorComparecimentos" x1="0" y1="0" x2="0" y2="1">
                      <stop offset="5%" stopColor="#8b5cf6" stopOpacity={0.3} />
                      <stop offset="95%" stopColor="#8b5cf6" stopOpacity={0} />
                    </linearGradient>
                    <linearGradient id="colorVendas" x1="0" y1="0" x2="0" y2="1">
                      <stop offset="5%" stopColor="#f59e0b" stopOpacity={0.3} />
                      <stop offset="95%" stopColor="#f59e0b" stopOpacity={0} />
                    </linearGradient>
                  </defs>
                  <CartesianGrid strokeDasharray="3 3" vertical={false} stroke={isDark ? '#374151' : '#f3f4f6'} />
                  <XAxis
                    dataKey="month"
                    axisLine={false}
                    tickLine={false}
                    tick={{ fill: isDark ? '#9ca3af' : '#6b7280', fontSize: 12 }}
                    dy={10}
                  />
                  <YAxis
                    axisLine={false}
                    tickLine={false}
                    tick={{ fill: isDark ? '#9ca3af' : '#6b7280', fontSize: 12 }}
                    tickFormatter={(value) => `${value}`}
                  />
                  <Tooltip
                    contentStyle={{
                      backgroundColor: isDark ? 'rgba(31, 41, 55, 0.95)' : 'rgba(255, 255, 255, 0.95)',
                      borderColor: isDark ? '#374151' : '#e5e7eb',
                      borderRadius: '0.75rem',
                      boxShadow: '0 4px 6px -1px rgba(0, 0, 0, 0.1), 0 2px 4px -1px rgba(0, 0, 0, 0.06)',
                      padding: '12px'
                    }}
                    itemStyle={{ color: isDark ? '#e5e7eb' : '#374151', paddingBottom: '4px' }}
                    labelStyle={{ color: isDark ? '#9ca3af' : '#6b7280', marginBottom: '8px', fontWeight: 600 }}
                    formatter={(value: number) => [formatNumberValue(value), '']}
                  />
                  <Legend
                    verticalAlign="top"
                    height={36}
                    iconType="circle"
                    wrapperStyle={{ paddingBottom: '20px' }}
                  />
                  <Area
                    type="monotone"
                    dataKey="totalLeads"
                    name="Leads"
                    stroke="var(--sminy-primary)"
                    fillOpacity={1}
                    fill="url(#colorLeads)"
                    strokeWidth={3}
                  />
                  <Area
                    type="monotone"
                    dataKey="totalAgendamentos"
                    name={`Agendamentos${agendamentosPorDataConsulta ? ' (data consulta)' : ''}`}
                    stroke="#10b981"
                    fillOpacity={1}
                    fill="url(#colorAgendamentos)"
                    strokeWidth={3}
                  />
                  <Area
                    type="monotone"
                    dataKey="totalComparecimentos"
                    name="Comparecimentos"
                    stroke="#8b5cf6"
                    fillOpacity={1}
                    fill="url(#colorComparecimentos)"
                    strokeWidth={3}
                  />
                  <Area
                    type="monotone"
                    dataKey="totalVendas"
                    name="Vendas"
                    stroke="#f59e0b"
                    fillOpacity={1}
                    fill="url(#colorVendas)"
                    strokeWidth={3}
                  />
                </AreaChart>
              </ResponsiveContainer>
            </div>
          </div>
        </div>
      </div>

      {/* Card adicional com métricas de conversão */}
      <div className={`rounded-xl shadow-sm border ${isDark ? 'bg-gray-800 border-gray-700' : 'bg-white border-gray-200'}`}>
        <div className={`p-4 border-b ${isDark ? 'border-gray-700' : 'border-gray-100'}`}>
          <h3 className={`text-lg font-semibold ${isDark ? 'text-white' : 'text-gray-800'}`}>Taxas de Conversão</h3>
          <p className={`text-sm ${isDark ? 'text-gray-400' : 'text-gray-500'}`}>Performance de conversão entre etapas</p>
        </div>
        <div className="p-4">
          <div className="grid grid-cols-1 md:grid-cols-3 gap-6">
            {/* Taxa de Conversão Leads para Agendamentos */}
            <div className={`flex flex-col items-center justify-center p-4 rounded-xl border hover:shadow-md transition-all ${isDark ? 'bg-gray-800 border-gray-700' : 'bg-white border-gray-200'}`}>
              <div className="text-4xl font-bold text-sminy-primary mb-2">
                {funnelMetrics && funnelMetrics.totalLeads > 0
                  ? ((totalAgendamentos / funnelMetrics.totalLeads) * 100).toFixed(1)
                  : '0.0'}%
              </div>
              <div className={`text-sm text-center ${isDark ? 'text-gray-400' : 'text-gray-500'}`}>
                Leads para Agendamentos
                {agendamentosPorDataConsulta && (
                  <span className={`text-xs block mt-1 ${isDark ? 'text-gray-500' : 'text-gray-400'}`}>(por data consulta)</span>
                )}
              </div>
            </div>

            {/* Taxa de Conversão Agendamentos para Comparecimentos */}
            <div className={`flex flex-col items-center justify-center p-4 rounded-xl border hover:shadow-md transition-all ${isDark ? 'bg-gray-800 border-gray-700' : 'bg-white border-gray-200'}`}>
              <div className="text-4xl font-bold text-emerald-500 mb-2">
                {totalAgendamentos > 0
                  ? ((funnelMetrics?.totalComparecimentos || 0) / totalAgendamentos * 100).toFixed(1)
                  : '0.0'}%
              </div>
              <div className={`text-sm text-center ${isDark ? 'text-gray-400' : 'text-gray-500'}`}>
                Agendamentos para Comparecimentos
              </div>
            </div>

            {/* Taxa de Conversão Comparecimentos para Vendas */}
            <div className={`flex flex-col items-center justify-center p-4 rounded-xl border hover:shadow-md transition-all ${isDark ? 'bg-gray-800 border-gray-700' : 'bg-white border-gray-200'}`}>
              <div className="text-4xl font-bold text-amber-500 mb-2">
                {funnelMetrics && funnelMetrics.totalComparecimentos > 0
                  ? ((funnelMetrics.totalVendas / funnelMetrics.totalComparecimentos) * 100).toFixed(1)
                  : '0.0'}%
              </div>
              <div className={`text-sm text-center ${isDark ? 'text-gray-400' : 'text-gray-500'}`}>
                Comparecimentos para Vendas
              </div>
            </div>
          </div>
        </div>
      </div>
    </div >
  );
};

export default OverviewTab;